import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";

import { DataGrid, GridColumns } from "@mui/x-data-grid";

import MaskedTypography from "../MaskedTypography/MaskedTypography";
import DocumentsCell from "../DocumentsCell/DocumentsCell";
import AddEditPassword from "../AddEditPassword/AddEditPassword";

import { Props } from "./interfaces";
import { Container } from "./styles";
import { LegalEntitySIDTO } from "../../../../../models/legal-entity-si";
import { Typography } from "@mui/material";
import { AddEditSharedWith } from "../AddEditSharedWith/AddEditSharedWith";
import { useAppSelector } from "../../../../../custom-hooks/redux/redux";
import { isAuthorizedLE } from "../../../../../custom-hooks/useAuthorization/useAuthorization";
import { AUTHORIZATIONS } from "../../../../../utils/constants/authorization-constants";

const Table = ({ items, totalItems, page, handleChangePage, handleNotifyParent }: Props) => {
  const { t } = useTranslation();
  const tRP = "pages.document_reports.compliance_documents.passwords.table.fields"; // tRP - translation root path

  const { currentUserAuthorizations } = useAppSelector((state) => state.currentUserReducer);

  const canChange = useCallback(
    (leIdToCheck: string) => {
      return (
        !!isAuthorizedLE([AUTHORIZATIONS.LegalEntityAccess.ManageLegalEntitySI], currentUserAuthorizations, [
          leIdToCheck,
        ]) || !!currentUserAuthorizations?.canChangeAll
      );
    },
    [currentUserAuthorizations]
  );

  const columns: GridColumns<LegalEntitySIDTO> = [
    {
      field: "createdByUser",
      headerName: t(`${tRP}.owner`),
      headerAlign: "center",
      renderCell: ({ row }) => <Typography>{row.createdByUser?.name ?? ""}</Typography>,
      width: 150,
      disableColumnMenu: true,
    },
    {
      field: "description",
      headerName: t(`${tRP}.description`),
      headerAlign: "center",
      width: 500,
      disableColumnMenu: true,
    },
    {
      field: "domain",
      headerName: t(`${tRP}.domain`),
      headerAlign: "center",
      width: 200,
      disableColumnMenu: true,
    },
    {
      field: "user",
      headerName: t(`${tRP}.user`),
      headerAlign: "center",
      width: 200,
      disableColumnMenu: true,
    },
    {
      field: "password",
      headerName: t(`${tRP}.password`),
      headerAlign: "center",
      width: 200,
      renderCell: ({ row }) => <MaskedTypography value={row.password} />,
      disableColumnMenu: true,
      sortable: false,
    },
    {
      field: "shared",
      headerName: t(`${tRP}.shared`),
      headerAlign: "center",
      width: 100,
      renderCell: ({ row }) => (
        <AddEditSharedWith
          legalEntitySI={row}
          handleNotifyParent={handleNotifyParent}
          readOnly={!(row.legalEntity?.id && canChange(row.legalEntity?.id))}
        />
      ),
      align: "center",
      disableColumnMenu: true,
      sortable: false,
    },
    {
      field: "files",
      headerName: t(`${tRP}.files`),
      headerAlign: "center",
      minWidth: 300,
      flex: 1,
      renderCell: ({ row }) => (
        <DocumentsCell
          legalEntitySI={row}
          handleNotifyParent={handleNotifyParent}
        />
      ),
      disableColumnMenu: true,
      sortable: false,
    },
    {
      field: "",
      headerName: t(`${tRP}.edit`),
      headerAlign: "center",
      align: "center",
      disableColumnMenu: true,
      sortable: false,
      renderCell: ({ row }) => (
        <>
          {row.legalEntity?.id && canChange(row.legalEntity?.id) && (
            <AddEditPassword
              effect="edit"
              passwordItem={row}
              handleNotifyParent={handleNotifyParent}
            />
          )}
        </>
      ),
    },
  ];

  return (
    <Container>
      <DataGrid
        columns={columns}
        rows={[...(items ?? [])]}
        rowCount={totalItems}
        onPageChange={(page) => handleChangePage("pageNumber", page)}
        onPageSizeChange={(size) => handleChangePage("pageSize", size)}
        page={page.pageNumber}
        pageSize={page.pageSize}
        rowsPerPageOptions={[2, 5, 10, 20, 50, 100]}
      />
    </Container>
  );
};

export default Table;
